import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { InfoGridProps } from "@/v2-backoffice-client/components/infoGrid"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import type { TenantViewQuery } from "@/v2-backoffice-client/tenant/view/__types__/tenant.view.query"
import CountryLabel from "@/v2-backoffice-client/country/CountryLabel"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import { deriveScopeUsageSignals } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import TenantPrivacyPresetBadge from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPresetBadge"
import { PRIVACY_POLICY_LABEL } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import { privacyPolicyLabel } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"

// The panel's read: the shared list fragment plus the member defaults and
// the personal-data summary the view query selects on top of it — see
// tenant.defaults.fragment.gql and tenant.query.gql for why those two are
// kept out of TenantFragment.
type TenantViewInfoTenant = NonNullable<TenantViewQuery["getTenantById"]>

const FIELDS = createInfoGridFields<TenantViewInfoTenant>({
  fields: {
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
    Name: {
      label: "Name",
      accessor: "Name",
      defaultHidden: true,
    },
    ExternalCustomerId: {
      label: "Hubspot Id",
      accessor: "ExternalCustomerId",
      to: ({ data }) => {
        if (!data.ExternalCustomerId) return
        return `${HUBSPOT_BASE_URL}/${data.ExternalCustomerId}`
      },
      defaultHidden: true,
    },
    // The Fenerum sibling of Hubspot Id: the owning customer's linked
    // billing account, linking to its Fenerum page.
    BillingAccountUuid: {
      label: "Fenerum Id",
      accessor: "BillingAccountUuid",
      to: ({ data }) => {
        if (!data.BillingAccountUuid) return
        return `${FENERUM_ACCOUNT_BASE_URL}/${data.BillingAccountUuid}/`
      },
      valueClassName: "truncate",
      defaultHidden: true,
    },
    CustomerName: {
      label: "Customer",
      accessor: "CustomerName",
      to: ({ data }) => {
        if (!data.ExternalCustomerId) return
        return `>>?v=customer:${data.ExternalCustomerId}`
      },
    },
    // Fusion-login migration progress over the team's members (FUS-927) —
    // same component and buckets as the teams board's column.
    LoginMigration: {
      label: "Login Migration",
      accessor: (data) => data.MigrationTotal ?? null,
      renderValue({ data }) {
        return <MigrationProgress rollup={data} className="max-w-56" />
      },
    },
    UserCount: {
      label: "Total Users",
      accessor: "UserCount",
    },
    ActiveUserCount: {
      label: "Active Users",
      accessor: "ActiveUserCount",
    },
    InactiveUserCount: {
      label: "Inactive Users",
      accessor: "InactiveUserCount",
    },
    RemovedUserCount: {
      label: "Removed Users",
      accessor: "RemovedUserCount",
    },
    InvitationCount: {
      label: "Invitations",
      accessor: "InvitationCount",
    },
    // The two usage stats promoted from the Activity tab — same badges as
    // the teams board and the tab's summary (one derivation, one
    // presentation). Both hide until the team has tracked usage.
    ChurnRisk: {
      label: "Churn Risk",
      // The derived level, not a raw column: null exactly when the badge
      // would be silent (no seats), so the field hides with it.
      accessor: (data) => {
        const level = deriveScopeUsageSignals(data)?.churnRisk.level
        return level && level !== "none" ? level : null
      },
      renderValue({ data }) {
        const signals = deriveScopeUsageSignals(data)
        if (!signals || signals.churnRisk.level === "none") return
        return (
          <UsageChurnRiskBadge
            level={signals.churnRisk.level}
            reasons={signals.churnRisk.reasons}
          />
        )
      },
    },
    Engagement: {
      label: "Engagement",
      accessor: (data) => deriveScopeUsageSignals(data)?.avgScore ?? null,
      renderValue({ data }) {
        const avgScore = deriveScopeUsageSignals(data)?.avgScore
        if (avgScore == null) return
        return <UsageScoreBadge score={avgScore} />
      },
    },
    // The team's personal-data policy (FUS-1184) — the same pill as the
    // teams board's column, read from the same cache record the Access
    // Control tab writes, so it moves the moment a preset is applied there.
    // Links to that tab; hides only when the summary itself failed to load.
    PrivacyPolicy: {
      label: PRIVACY_POLICY_LABEL,
      accessor: (data) => privacyPolicyLabel(data.PrivateDataVisibilitySummary),
      to: () => "?tenant-view-tabs-tab=access-control",
      renderValue({ data }) {
        return (
          <TenantPrivacyPresetBadge
            summary={data.PrivateDataVisibilitySummary}
          />
        )
      },
    },
    // What the next member to join gets. Shown even when unset, because
    // "this team hands out nothing" is the thing an operator chasing a
    // misplaced user needs to see.
    DefaultProject: {
      label: "New Member Project",
      accessor: (data) => data.DefaultProject?.Name ?? data.DefaultProjectId,
      renderValue({ data }) {
        if (!data.DefaultProjectId) {
          return <span className="text-muted-foreground">Not set</span>
        }
        if (!data.DefaultProject) {
          return (
            <span className="text-muted-foreground">
              #{data.DefaultProjectId} (missing project)
            </span>
          )
        }
        return data.DefaultProject.IsDeleted ? (
          <DotText variant="destructive">
            {data.DefaultProject.Name} (deleted)
          </DotText>
        ) : (
          data.DefaultProject.Name
        )
      },
    },
    DefaultCountry: {
      label: "New Member Country",
      accessor: "DefaultCountryCode",
      renderValue: ({ data }) => (
        <CountryLabel
          code={data.DefaultCountryCode}
          emptyLabel={data.DefaultProjectId ? "Follows the project" : "Not set"}
        />
      ),
    },
    IsDeleted: {
      label: "Deleted",
      accessor: "IsDeleted",
      renderValue: ({ value }) => (
        <DotText variant={value ? "destructive" : "success-1"}>
          {value ? "Yes" : "No"}
        </DotText>
      ),
    },
  },
})

export type TenantViewInfoProps = Omit<
  InfoGridProps<TenantViewInfoTenant>,
  "data" | "fields"
> & {
  tenant: TenantViewInfoTenant | null | undefined
}

function TenantViewInfo(props: TenantViewInfoProps) {
  const { tenant, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="tenant-info"
      className={cn("tenant-view-info", className)}
      data={tenant}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default TenantViewInfo
